import React, { useMemo, useState } from 'react';
import { Button, FloatingLabel, Form, ModalBody } from 'react-bootstrap';

interface RetestCompletionDateProps {
	close: () => void;
	confirm: (requestedCompletionDate: string) => void;
}

// FloatingLabel with a controlId rather than the shared FloatingField: FloatingField renders its
// label with nothing tying it to the input, and a lone date box needs a label that names it to
// assistive tech. The result is the same floating-label date field the other project popups use.
const RetestCompletionDate: React.FC<RetestCompletionDateProps> = ({ close, confirm }) => {
	// A retest cannot be delivered on the day it is asked for, so the earliest selectable date is
	// tomorrow. The server applies the same rule -- the admin portal posts to the same endpoint.
	const earliest = useMemo(() => {
		const date = new Date();
		date.setDate(date.getDate() + 1);
		return date.toISOString().split('T')[0];
	}, []);

	const [requestedCompletionDate, setRequestedCompletionDate] = useState('');

	// `min` only stops the picker's own arrows; a date can still be typed or pasted in, so the
	// rule is enforced here too rather than left to the browser.
	const isTooSoon = requestedCompletionDate.length > 0 && requestedCompletionDate < earliest;

	return (
		<ModalBody className='my-3 pt-3 px-xl-5'>
			<p>When does this retest need to be completed by?</p>

			<FloatingLabel controlId='retest-completion-date' label='Complete Retest By' className='mb-4'>
				<Form.Control
					required
					autoFocus
					type='date'
					min={earliest}
					value={requestedCompletionDate}
					isInvalid={isTooSoon}
					onChange={(e) => setRequestedCompletionDate(e.target.value)}
				/>
				<Form.Control.Feedback type='invalid'>
					The completion date must be later than today.
				</Form.Control.Feedback>
			</FloatingLabel>

			<div className='d-flex justify-content-end gap-2'>
				<Button variant='outline-secondary' onClick={close}>Cancel</Button>
				<Button
					disabled={requestedCompletionDate.length === 0 || isTooSoon}
					onClick={() => confirm(requestedCompletionDate)}
				>
					Submit Request
				</Button>
			</div>
		</ModalBody>
	);
};

export default RetestCompletionDate;
